const _ = require('lodash');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PaymentSaveError = require('@/lib/Payments/PaymentSaveError');
const { apiResponseToError } = require('@/lib/Api');
const { formatDateForApi } = require('@/lib/Dates/dateFunctions');
const { hydrateArray } = require('@/lib/Hydration');

/**
 * @typedef {Object} PaymentForApiStructure
 * @property {number} amount
 * @property {number} tendered
 * @property {number} changeGiven
 * @property {number} originalPaymentID
 * @property {number} paymentChannelID
 * @property {number} paymentMethodID
 * @property {boolean} supportsPartialRefunds
 * @property {string} thirdPartyID
 * @property {number} tradingSessionID
 */

/**
 * @typedef {Object} RefundPaymentForApiStructure
 * @property {number} originalPaymentID
 * @property {number} amount
 * @property {number} paymentChannelID
 * @property {string} thirdPartyID
 * @property {number} tradingSessionID
 */

/**
 * @constructor
 *
 * @param {ApiClient} apiClient
 * @param {AppConfig} appConfig
 * @param {CurrentDevice} currentDevice
 * @param {Hydrator} hydrator
 * @param {PartialRefundPaymentResource} partialRefundPaymentResource
 * @param {PaymentResource} paymentResource
 */
const PaymentService = function (
    apiClient,
    appConfig,
    currentDevice,
    hydrator,
    partialRefundPaymentResource,
    paymentResource
) {
    this.apiClient = apiClient;
    this.appConfig = appConfig;
    this.currentDeviceService = currentDevice;
    this.hydrator = hydrator;
    this.paymentResource = paymentResource;
    this.partialRefundPaymentResource = partialRefundPaymentResource;

    /**
     * Array of payment method refs that are allowed to have zero value if they are not the only payment in payments[].
     * For example a tax on sale Experience voucher may have zero value.
     *
     * @type {string[]}
     */
    this.allowedZeroAmountPaymentMethods = [
        PaymentMethodRef.GIFT_VOUCHER,
    ];
};

PaymentService.prototype = {
    /**
     * Saves multiple payments against an order.
     * It will only save new payments (those without an ID) and will ignore any existing payments.
     *
     * @param {number} orderID
     * @param {Payment[]} payments
     *
     * @return {Promise<Payment[]>} Returns *every* payment for the order, not only those we just saved.
     */
    storePaymentsForOrder(orderID, payments) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            // Filter out any payments that already have an ID,
            // and convert them all to the API structure.
            const paymentsForApi = this.convertPaymentsToApiStructure(
                payments.filter((p) => !p.ID)
            );

            // We need to split the payments into an array of standard payments and an array of refund payments because
            // they will be sent to different API endpoints.
            const [serverProcessedPayments, clientProcessedPayments] = _.partition(
                paymentsForApi,
                (p) => p.supportsPartialRefunds && p.amount < 0.00
            );

            let responsePayments = null;
            let hydratedPayments = null;
            let isRefund = false;

            // Error for a refund (no payments at this time) that is taken on the server e.g. SagePay refund.
            // ProPoint makes a request to the API which then carries out actions to process the refund.
            let serverProcessedPaymentsError = null;
            // Error for a payment or refund taken on the client e.g. Chip & Pin, Cash, etc.
            let clientProcessedPaymentsError = null;

            // When the we attempt to store the payment it has already been taken so a failure here does not mean that
            // the payment has failed, it means that there has been an API error when we attempt to store the payment.
            // TODO: do we want to submit normal payments individually like we do for refund payments?
            if (clientProcessedPayments.length) {
                isRefund = clientProcessedPayments[0].amount < 0;
                await this
                    .storeClientProcessedPayments(orderID, clientProcessedPayments)
                    .then((result) => {
                        responsePayments = result;
                    })
                    .catch(async (error) => {
                        clientProcessedPaymentsError = error;

                        // Try and get the payments that exist in the database for this order.
                        responsePayments = await this.getPaymentsByOrderID(orderID).catch(() => {
                            // We are already trying to handle an error so if there is an error here there is probably
                            // a problem with the API. This is different to the way we handle serverProcessedPayments
                            // because if the clientProcessedPayments were saved the payments are returned anyway.
                        });
                    });
            }

            // A refund payment is processed on the server, so if this API request fails it means that the refund has
            // failed and we need to try again.
            if (serverProcessedPayments.length) {
                await this
                    .storeServerProcessedPayments(serverProcessedPayments)
                    .catch((err) => {
                        serverProcessedPaymentsError = err;
                    });

                // Because we are submitting individual payments we do not receive the complete collection of payments
                // in the response so we will need to make another query to fetch them.
                responsePayments = await this
                    .getPaymentsByOrderID(orderID)
                    .catch((err) => {
                        if (!serverProcessedPaymentsError) {
                            // Only here if the refund request was successful but the request to get payments failed.
                            reject(new PaymentSaveError(
                                PaymentSaveError.SERVER_REFUND_GET_PAYMENTS_ERROR,
                                err
                            ));
                        }
                    });
            }

            if (responsePayments && responsePayments.length) {
                hydratedPayments = this.hydrator.hydrateArray(
                    responsePayments,
                    () => new Payment()
                );
            }

            if (clientProcessedPaymentsError) {
                reject(new PaymentSaveError(
                    (isRefund
                        ? PaymentSaveError.PERSIST_CLIENT_REFUND_ERROR
                        : PaymentSaveError.PERSIST_CLIENT_PAYMENT_ERROR
                    ),
                    clientProcessedPaymentsError
                ).setHydratedConfirmedPayments(hydratedPayments));
                return;
            }

            if (serverProcessedPaymentsError) {
                reject(new PaymentSaveError(
                    PaymentSaveError.SERVER_REFUND_ERROR,
                    serverProcessedPaymentsError
                ).setHydratedConfirmedPayments(hydratedPayments));
                return;
            }

            resolve(hydratedPayments);
        });
    },

    /**
     * Send any 'clientProcessedPayments' e.g. chip & pin, cash, etc. to the /v2/payments endpoint.
     *
     * @param {number} orderID
     * @param {PaymentForApiStructure[]} payments Payment data to send to API
     *
     * @return {Promise<[]>} Returns every payment for the order, as the raw API data (not hydrated Payments).
     */
    storeClientProcessedPayments(orderID, payments) {
        return this.apiClient
            .request(
                'POST',
                'payments',
                {
                    orderID,
                    payments,
                }
            )
            .then(({ data }) => {
                if (data?.success) {
                    return data.payments;
                }
                throw new Error(data.error || data.message || '');
            });
    },

    /**
     * Make sequential refund payment requests. Reject on 1st failure as it is easier to handle a single failure than
     * multiple.
     *
     * @param {PaymentForApiStructure[]} refundPayments
     *
     * @returns {Promise<[]>}
     */
    async storeServerProcessedPayments(refundPayments) {
        let paymentPromises = [];

        // eslint-disable-next-line unused-imports/no-unused-vars
        for (const refundPayment of refundPayments) {
            try {
                let paymentPromise = await this.storePartialRefundPayment(refundPayment);
                paymentPromises.push(paymentPromise);
            } catch (error) {
                throw new Error(error);
            }
        }

        return paymentPromises;
    },

    /**
     * Send a single payment that support partial refunds to the /v2/refunds endpoint.
     *
     * This is for payment types like SagePayServer where the refund is carried out by App.
     *
     * The alternative approach is a payment type like Clover where the refund is carried out on the terminal and
     * triggered from ProPoint we just send the completed refund to the /v2/payments to be recorded.
     *
     * @param {PaymentForApiStructure} payment Payment data to send to the API
     *
     * @return {Promise}
     */
    storePartialRefundPayment(payment) {
        return new Promise((resolve, reject) => {
            const refundPayment = this.refundPaymentForApi(payment);

            this.partialRefundPaymentResource.store(
                {},
                {
                    ...refundPayment,
                },
                (response) => {
                    if (response && response.success) {
                        resolve();
                    } else {
                        reject(new Error(response.error || response.message || ''));
                    }
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * @param orderID
     *
     * @returns {Promise<[]>}
     */
    getPaymentsByOrderID(orderID) {
        return new Promise((resolve, reject) => {
            this.paymentResource.findByOrderID(
                {
                    orderID,
                },
                (response) => {
                    resolve(response);
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * @param {string} bookingRef
     *
     * @returns {Payment[]}
     */
    getPaymentsByBookingRef(bookingRef) {
        return this.apiClient
            .request(
                'GET',
                'orders/' + bookingRef,
                {
                    resolve: 'paymentlines,paymentlines.paymentmethods',
                },
            )
            .then(async ({ data }) => {
                return hydrateArray(
                    data.paymentlines,
                    () => new Payment()
                );
            });
    },

    /**
     * Returns all new payments in the cart (those without an ID) after they have been cleaned.
     *
     * @param {Cart} cart
     *
     * @returns {Payment[]}
     */
    getAllNewPaymentsFromCart(cart) {
        let payments = cart.payments
            .filter((p) => !p.ID);

        return this.cleanPayments(payments);
    },

    /**
     * Returns an array of non-GoCardless new Payment models from the cart after they have been cleaned.
     *
     * @param {Cart} cart
     *
     * @returns {Payment[]}
     */
    getNewPaymentsFromCartExcludingGoCardless(cart) {
        // Exclude GoCardless payments.
        // See https://digitickets.atlassian.net/browse/PROP-2501
        return this.getAllNewPaymentsFromCart(cart).filter(
            (payment) => payment.paymentMethod?.ref !== PaymentMethodRef.GOCARDLESS
        );
    },

    /**
     * @param {Cart} cart
     *
     * @returns {Payment[]}
     */
    getNewGoCardlessPaymentsFromCart(cart) {
        // Only include GoCardless payments.
        // See https://digitickets.atlassian.net/browse/PROP-2501
        return this.getAllNewPaymentsFromCart(cart).filter(
            (payment) => payment.paymentMethod?.ref === PaymentMethodRef.GOCARDLESS
        );
    },

    /**
     * @param {Payment[]} payments
     *
     * @returns {PaymentForApiStructure[]}
     */
    convertPaymentsToApiStructure(payments) {
        return payments.map((p) => this.convertPaymentToApiStructure(p));
    },

    /**
     * @param {Payment} payment
     *
     * @return {PaymentForApiStructure}
     */
    convertPaymentToApiStructure(payment) {
        // Since PROP-738 we now specify exactly what to send to the API
        // instead of sending everything the Payment contains.
        const p = {
            ID: payment.ID,
            amount: payment.amount,
            authCode: payment.authCode,
            cardTypeRef: payment.cardTypeRef,
            cashback: payment.cashback,
            changeGiven: payment.changeGiven,
            debugInfo: payment.debugInfo,
            gratuity: payment.gratuity,
            // lastDigits: payment.lastDigits, // API doesn't support this, so don't send it for data protection.
            originalPaymentID: payment.originalPaymentID,
            outstandingRefundableAmount: payment.outstandingRefundableAmount,
            paid: payment.paid,
            // Set the paymentChannelID and don't send the entire paymentChannel object.
            paymentChannelID: this.appConfig.channelID,
            paymentCompositeID: payment.paymentCompositeID,
            paymentMethodID: payment.paymentMethod ? payment.paymentMethod.ID : null,
            paymentTokenID: payment.paymentTokenID,
            pendingPartialRefundAmount: payment.pendingPartialRefundAmount,
            securityKey: payment.securityKey,
            status: payment.status,
            // Put some spaces into statusDetail it can reflow when viewed in places like Back Office.
            // Note: This is just a lo-fi fix rather than using JSON.stringify(obj, null, " ") as this field comes in a
            // variety of formats. Could we / should we think about converting all statusDetails to JSON?
            statusDetail: payment.statusDetail ? payment.statusDetail.replaceAll('":', '": ') : null,
            // Add this property to the individual payment, so we can easily split the results later.
            supportsPartialRefunds: payment.paymentMethod && payment.paymentMethod.supportsPartialRefunds,
            takenAt: formatDateForApi(payment.takenAt),
            tendered: payment.tendered,
            thirdPartyID: payment.thirdPartyID,
            token: payment.token,
            tradingSessionID: this.currentDeviceService.getTradingSessionID(),
            transactionRef: payment.transactionRef,
            value: payment.value,
        };

        if (payment.paymentMethod && payment.paymentMethod.supportsChange) {
            // If any cash payments have a negative tendered amount, move that to the changeGiven amount.
            if (p.tendered < 0) {
                p.changeGiven += Math.abs(p.tendered);
                p.tendered = 0;
                p.amount = -p.changeGiven;
            }
        }

        return p;
    },

    /**
     * This does the same thing CartService.removeEmptyPayments and cartService.exportPayments did previously.
     *
     * Remove any payments from the cart that have 0 tendered and 0 changeGiven unless they are from a legitimate
     * zero value payment such as a tax on sale experience gift voucher.
     *
     * If this leaves us with no payments at all and there ARE some zero value payments keep just one of them.
     *
     * @param {Payment[]} payments
     *
     * @return {Payment[]} payments
     */
    cleanPayments(payments) {
        // Recalculate the amount property of the payment.
        payments.forEach((p) => p.calculateAmount());

        const nonZeroPayments = payments.filter(
            (p) => p.amount !== 0 || this.allowedZeroAmountPaymentMethods.includes(p.paymentMethod.ref)
        );

        // If there are some non-zero payments keep all those and ignore the rest (which will be zero value).
        if (nonZeroPayments.length > 0) {
            // Remove any duplicate payments by putting into a set.
            return [...new Set(nonZeroPayments)];
        }

        // If there are only zero value payments keep the first one so we have at least 1 payment and lose the rest.
        if (payments.length > 0) {
            return [
                payments[0],
            ];
        }

        return [];
    },

    /**
     * Create the refund data structure we need for the API.
     *
     * @param {PaymentForApiStructure} payment
     *
     * @return {RefundPaymentForApiStructure}
     */
    refundPaymentForApi(payment) {
        return {
            originalPaymentID: payment.originalPaymentID,
            amount: payment.amount * -1,
            paymentChannelID: payment.paymentChannelID,
            thirdPartyID: payment.thirdPartyID,
            tradingSessionID: this.currentDeviceService.getTradingSessionID(),
        };
    },
};

module.exports = PaymentService;
